import { useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { api } from '../api';
import type { RateVectorInput, RenewLeaseInput } from '../types';
import { Modal } from './Modal';

type Props = {
  leaseId: number;
  leaseLabel: string;
  currentExpiration: string | null;
  onClose: () => void;
  onRenewed: () => Promise<void> | void;
};

type FormShape = {
  renewalDate: string;
  newExpirationDate: string;
  newEscalationPct: string; // %; converted to fraction on submit
  notes: string;
  external_rate: string;
  internal_rate: string;
  mezzanine_rate: string;
  misc_rate: string;
  umbrella_rate: string;
  covered_area_rate: string;
  store_room_rate: string;
  balcony_rate: string;
};

const rateFields: { key: keyof RateVectorInput; label: string }[] = [
  { key: 'external_rate', label: 'External rate' },
  { key: 'internal_rate', label: 'Internal rate' },
  { key: 'mezzanine_rate', label: 'Mezzanine rate' },
  { key: 'misc_rate', label: 'Misc rate' },
  { key: 'umbrella_rate', label: 'Umbrella rate' },
  { key: 'covered_area_rate', label: 'Covered area rate' },
  { key: 'store_room_rate', label: 'Store room rate' },
  { key: 'balcony_rate', label: 'Balcony rate' },
];

// % string -> fraction number (3 -> 0.03), matching how leases.escalation is stored.
const pctToFraction = (v: string): number | null => {
  if (v.trim() === '') return null;
  const n = Number(v) / 100;
  if (!Number.isFinite(n)) return null;
  return Number(n.toFixed(4));
};

const today = (): string => new Date().toISOString().slice(0, 10);

export const RenewLeaseDialog = ({
  leaseId,
  leaseLabel,
  currentExpiration,
  onClose,
  onRenewed,
}: Props) => {
  const [showRates, setShowRates] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, formState } = useForm<FormShape>({
    defaultValues: {
      renewalDate: today(),
      newExpirationDate: '',
      newEscalationPct: '',
      notes: '',
      external_rate: '',
      internal_rate: '',
      mezzanine_rate: '',
      misc_rate: '',
      umbrella_rate: '',
      covered_area_rate: '',
      store_room_rate: '',
      balcony_rate: '',
    },
  });

  const submit: SubmitHandler<FormShape> = async (data) => {
    setError(null);
    if (data.newExpirationDate <= data.renewalDate) {
      setError('New expiration date must be after the renewal date.');
      return;
    }
    const input: RenewLeaseInput = {
      renewalDate: data.renewalDate,
      newExpirationDate: data.newExpirationDate,
    };
    const newEscalation = pctToFraction(data.newEscalationPct);
    if (newEscalation !== null) input.newEscalation = newEscalation;
    if (data.notes.trim() !== '') input.notes = data.notes.trim();
    // Only submit resetRates if the section was opened and at least one rate
    // was filled in; blank fields in a used vector default to 0.
    if (showRates && rateFields.some((f) => data[f.key].trim() !== '')) {
      const rates = {} as RateVectorInput;
      for (const f of rateFields) {
        const n = Number(data[f.key]);
        rates[f.key] = Number.isFinite(n) ? n : 0;
      }
      input.resetRates = rates;
    }
    setBusy(true);
    try {
      await api.renewLease(leaseId, input);
      await onRenewed();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'renewal failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Renew lease — ${leaseLabel}`} onClose={onClose}>
      <form
        noValidate
        className={formState.isSubmitted ? 'submitted' : ''}
        onSubmit={(e) => {
          void handleSubmit(submit)(e);
        }}
      >
        <div className="form-grid">
          {currentExpiration && (
            <div className="full muted" style={{ fontSize: 13 }}>
              Current expiration: {currentExpiration}
            </div>
          )}
          <div className="field">
            <label>Renewal date</label>
            <input type="date" {...register('renewalDate', { required: true })} />
          </div>
          <div className="field">
            <label>New expiration date</label>
            <input type="date" {...register('newExpirationDate', { required: true })} />
          </div>
          <div className="field">
            <label>New escalation (%) — optional</label>
            <input type="number" step="0.01" {...register('newEscalationPct')} />
            <span className="muted" style={{ fontSize: 12 }}>
              Applies to escalations after the renewal date. Leave blank to keep the current
              escalation.
            </span>
          </div>
          <div className="field full">
            <label>Notes</label>
            <textarea rows={2} {...register('notes')} />
          </div>

          <div className="full">
            <button type="button" onClick={() => setShowRates((v) => !v)}>
              {showRates ? 'Hide rate reset' : 'Reset rates…'}
            </button>
          </div>
          {showRates && (
            <>
              <div className="full muted" style={{ fontSize: 12 }}>
                Optional: rebase the rent schedule from the renewal date. Leave every field
                blank to keep the current rates; a blank field in a used reset counts as 0.
              </div>
              {rateFields.map((f) => (
                <div className="field" key={f.key}>
                  <label>{f.label}</label>
                  <input type="number" step="0.01" {...register(f.key)} />
                </div>
              ))}
            </>
          )}

          {error && <div className="error full">{error}</div>}
          <div className="full" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Renewing…' : 'Renew lease'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
